import * as React from 'react';

import { cn } from '../../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  required?: boolean;
  error?: string;
  rows?: number;
  /** Disable only for editors with an explicit fixed-height layout. */
  autoSize?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, required, error, rows = 3, autoSize = true, id, className, onInput, style, ...props },
  ref,
) {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const setRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const resize = React.useCallback(() => {
    const node = textareaRef.current;
    if (!node || !autoSize) return;
    const computed = window.getComputedStyle(node);
    const number = (value: string) => Number.parseFloat(value) || 0;
    const lineHeight = number(computed.lineHeight) || (number(computed.fontSize) || 14) * 1.5;
    const padding = number(computed.paddingTop) + number(computed.paddingBottom);
    const border = number(computed.borderTopWidth) + number(computed.borderBottomWidth);
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const viewportLimit = Math.max(48, Math.min(320, viewportHeight * 0.4));
    const configuredLimit = number(computed.maxHeight) || viewportLimit;
    const limit = Math.min(viewportLimit, configuredLimit);
    const minimum = Math.min(
      limit,
      Math.max(rows * lineHeight + padding + border, number(computed.minHeight)),
    );
    node.style.height = '0px';
    const contentHeight = node.scrollHeight + border;
    node.style.height = `${Math.min(limit, Math.max(minimum, contentHeight))}px`;
    node.style.overflowY = contentHeight > limit ? 'auto' : 'hidden';
  }, [autoSize, rows]);

  React.useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!autoSize && node) {
      node.style.height =
        typeof style?.height === 'number' ? `${style.height}px` : (style?.height ?? '');
      node.style.overflowY = style?.overflowY ?? '';
      return;
    }
    resize();
  });
  React.useEffect(() => {
    if (!autoSize) return;
    const node = textareaRef.current;
    let width = node?.getBoundingClientRect().width;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => {
            const nextWidth = node?.getBoundingClientRect().width;
            if (nextWidth !== width) {
              width = nextWidth;
              resize();
            }
          });
    if (node) observer?.observe(node);
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    let resetFrame: number | undefined;
    const afterReset = () => {
      resetFrame = requestAnimationFrame(resize);
    };
    node?.form?.addEventListener('reset', afterReset);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
      node?.form?.removeEventListener('reset', afterReset);
      if (resetFrame !== undefined) cancelAnimationFrame(resetFrame);
    };
  }, [autoSize, resize]);

  return (
    <div className="flex w-full flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="mb-2 block text-sm font-medium text-secondary-950">
          {label}
          {required && (
            <span className="text-error-500 ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <textarea
        {...props}
        ref={setRef}
        id={inputId}
        rows={rows}
        aria-describedby={
          [props['aria-describedby'], errorId].filter(Boolean).join(' ') || undefined
        }
        aria-invalid={error ? true : props['aria-invalid']}
        className={cn(
          'w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-body',
          'focus:outline-none focus:ring-2 focus:ring-primary-500',
          error && 'border-error-500',
          autoSize && 'resize-none',
          className,
        )}
        style={style}
        onInput={(event) => {
          resize();
          onInput?.(event);
        }}
      />
      {error && (
        <p id={errorId} className="mt-1 text-xs text-error-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
